<template>
  <div
    class="pure-g root"
    :class="[brandExclusion]"
  >
    <AppRefreshIndicator ref="refresh" />
    <div class="head pure-u-1">
      <div
        class="impersonation"
        v-if="isImpersonated && accessToken"
        v-text="impersonatedText"
      />
      <compHeader
        class="header"
        :important-messages="importantMessages"
        @reload-translations="reloadTranslations"
        @change-customer="handleCustomerChange"
      />
      <SideButtons @click="openPopup" />
      <PopupBase
        class="contactform"
        :show="contactFormOverlay"
        :size="popupSizeMedium"
        @close-pop-up="contactFormOverlay = false"
      >
        <DynamicsContactForm
          v-if="contactFormOverlay"
          :prefilled-message="contactFormPrefilledMessage"
          :initial-topic-id="contactFormTopicId"
          :tracking-event-callback="contactFormTrackingCallback"
          @close="closeContactForm()"
        />
      </PopupBase>
      <PopupBrands
        :show="showBrandsPopup"
        :brands="brands"
        @close-pop-up="showBrandsPopup = false"
      />
      <PopupRegions />
      <PopupHydraulicSelectorForm
        :size="popupSize.small"
        :show="showHydraulicSelectorForm"
        :selector-data="hydraulicSelectorData"
        @close-pop-up="closeHydraulicSelectorForm()"
      />
      <PopupMarkdownBasic
        :show="showTariffDisclaimer"
        :size="popupSize.small"
        :headline="$tv('headline', 'tariffDisclaimer')"
        :text="$tv('text', 'tariffDisclaimer')"
        @close-pop-up="acceptedTariffDisclaimer = true"
      />
      <PopupMarkdownBasic
        v-for="(message, index) in targetedMessages"
        :key="`targeted-message-${index}`"
        :show="true"
        :size="popupSize.small"
        :headline="message.Topic"
        :text="message.Text"
        @close-pop-up="dismissTargetedMessage(index)"
      />
      <div
        :class="{
          'head__spacing--xs': isReady && ((!this.$auth.loggedIn && !isProductPage) || (this.$auth.loggedIn && !hasCompanies && !isProductPage)),
          'head__spacing--s': ((!isReady && !isProductPage) || (isReady && this.$auth.loggedIn && !isProductPage && hasCompanies)),
          'head__spacing--m': isReady && ((!this.$auth.loggedIn && isProductPage) || (this.$auth.loggedIn && !hasCompanies && isProductPage)),
          'head__spacing--l': ((!isReady && isProductPage) || (isReady && this.$auth.loggedIn && isProductPage && hasCompanies))
        }"
      >
        <ImportantMessages
          v-if="importantMessages"
          :important-messages="importantMessages"
          class="head__notification__mobile"
        />
        <main class="pure-u-1">
          <div :style="{'margin-top': headerHeight }">
            <slot />
          </div>
          <div
            class="modal-container"
          />
        </main>
      </div>
    </div>
    <div
      v-if="showAddressDropDown"
      class="bottom__overlay"
    >
      <OverlayAddressSelector
        :addresses="addresses"
        :key="componentKey"
      />
    </div>
    <compFooter
      :class="{ addressMargin: showAddressDropDown }"
    />
    <div
      id="appversion"
      v-text="appVersion"
    />
  </div>
</template>

<script>
import Header from '~/components/header/Header'
import ImportantMessages from '~/components/header/ImportantMessages'
import Footer from '~/components/footer/Footer'
import {storeToRefs} from 'pinia'
import {IntlService, load} from '@progress/kendo-vue-intl'
import {supplemental, main} from '~/config/localedata.json'
import SideButtons from '~/components/home/SideButtons.vue'
import {SIDEBAR_ACTIONS, POPUP} from '~/assets/js/constants'
import PopupBase from '~/components/popup/PopupBase'
import DynamicsContactForm from '~/components/popup/DynamicsContactForm.vue'
import PopupHydraulicSelectorForm from '~/components/popup/PopupHydraulicSelectorForm.vue'
import PopupBrands from '~/components/popup/PopupBrands'
import {getRegionForSalesArea} from '~/assets/js/utils/region_service'
import AppRefreshIndicator from '~/components/loading-indicators/app-refresh-indicator.vue'
import {useBrand} from '~/composables/useBrand.ts'
import OverlayAddressSelector from '~/components/cart/OverlayAddressSelection.vue'
import {useCustomerService} from '@/composables/customer_service'
import {ADDRESS_TYPE} from '~/assets/js/constants'
import jwt_decode from 'jwt-decode'
import {useUserStore} from '@/store/user'
import {useCartStore} from '@/store/cart'
import {useImportantMessagesStore} from '@/store/important-messages'
import {useTargetedMessagesStore} from '@/store/targeted-messages'
import {useProductDataStore} from '@/store/product-data'
import {getMatchedComponents} from '~/assets/js/authentication'
import PopupRegions from '~/components/popup/PopupRegions.vue'
import {useCanonicalPathHelper} from '~/composables/canonical_path_helper'
import {useConfigurationDataStore} from '@/store/configuration-data'
import {useTariffHelper} from '~/composables/tariff_helper'
import useI18nExtended from '~/composables/usei18nExtended'

export default {
  components: {
    'compFooter': Footer,
    'compHeader': Header,
    ImportantMessages,
    SideButtons,
    PopupBase,
    DynamicsContactForm,
    PopupHydraulicSelectorForm,
    AppRefreshIndicator,
    PopupBrands,
    OverlayAddressSelector,
    PopupRegions
  },
  setup() {
    const cartStore = useCartStore()
    const {getAddresses} = useCustomerService()
    const {companies, loadedCustomerUuid, selectedCustomer, excludedBrands, accessToken, isInternalUser, companyNo, salesAreaName, isLoadingUserData, isSettingCustomerData} = storeToRefs(useUserStore())
    const {messages} = storeToRefs(useImportantMessagesStore())
    const targetedMessagesStore = useTargetedMessagesStore()
    const {visibleMessages: targetedMessages} = storeToRefs(targetedMessagesStore)
    const configurationDataStore = useConfigurationDataStore()
    const {acceptedTariffDisclaimer} = storeToRefs(configurationDataStore)
    const {isTariffEnabled, isTariffDisclaimerEnabled} = useTariffHelper()
    const {getBrandByCode, getBrandByName} = useBrand()

    watch(selectedCustomer, (newCustomer, oldCustomer) => {
      if (oldCustomer?.customerUuid && newCustomer?.customerUuid !== oldCustomer?.customerUuid && configurationDataStore.experimentalMode) {
        configurationDataStore.experimentalMode = false
      }
    }, {deep: true})

    return {
      getAddresses,
      cartStore,
      targetedMessagesStore,
      companies,
      currentCustomerUuid: loadedCustomerUuid,
      selectedCustomer,
      excludedBrands,
      accessToken,
      importantMessages: messages,
      targetedMessages,
      isInternalUser,
      companyNo,
      salesAreaName,
      acceptedTariffDisclaimer,
      isTariffEnabled,
      isTariffDisclaimerEnabled,
      isLoadingUserData,
      isSettingCustomerData,
      getBrandByCode,
      getBrandByName
    }
  },
  mounted() {
    this.$gtm.enable(true)
    if (this.$auth.loggedIn) {
      this.$gtm.trackEvent({
        event: 'login',
        category: 'login',
        action: 'login',
        companyNo: this.companyNo,
        salesAreaName: this.salesAreaName,
        userType: this.isInternalUser ? 'internal' : 'external'
      })
    }
    window.addEventListener('scroll', this.handleScroll)
    const bodyRect = document.body.getBoundingClientRect()
    this.isMobile = bodyRect.width < 768
    this.$eventbus.on('showHydraulicSelectorForm', (data = {}) => {
      this.showHydraulicSelectorForm = true
      this.hydraulicSelectorData = data
      this.$gtm.trackEvent({
        event: 'hydra_contactform_sendrequest',
        category: 'hydraulic-selector',
        action: 'open_contact_form'
      })
    })
    this.setupImportantMessagesObserver()
  },
  beforeUnmount() {
    this.$eventbus.off('showHydraulicSelectorForm')
    this.$eventbus.off('showBrandsPopup')
    this.$eventbus.off('hideBrandsPopup')
    this.$eventbus.off('showContactPopup')
    if (typeof window !== 'undefined') {
      window.removeEventListener('scroll', this.handleScroll)
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    // const el = document.getElementById('surveySubmitBtn')
    // if (el) {
    //   el.removeEventListener('click', this.sendGTMEvent())
    // }
  },
  computed: {
    head() {
      const componentName = getMatchedComponents(this.$route)[0]?.name
      const translationKeyTitle = componentName && `${componentName}Title`
      const translationKeyDescription = componentName && `${componentName}Description`
      const translationFallbackDescription = 'fallbackDescription'

      const i18nHead = useLocaleHead({addSeoAttributes: true}).value
      useCanonicalPathHelper().adjustCanonicalPath(this.$route.path, i18nHead)

      let description = ''
      if (translationKeyDescription && this.$te('seo.' + translationKeyDescription)) {
        description = this.$tv(translationKeyDescription, 'seo')
      } else if (this.$te('seo.' + translationFallbackDescription)) {
        description = this.$tv(translationFallbackDescription, 'seo')
      }

      return {
        title: translationKeyTitle && this.$te('seo.' + translationKeyTitle) ? this.$tv(translationKeyTitle, 'seo') : this.$tv('title', 'seo'),
        categoryLanguage: this.$i18n.locale,
        htmlAttrs: {
          ...i18nHead.htmlAttrs
        },
        meta: [
          ...i18nHead.meta,
          {
            hid: 'description',
            name: 'description',
            content: description
          }
        ],
        link: [...i18nHead.link]
      }
    },
    isProductPage() {
      return this.$route.name?.includes('products-product')
    },
    isSearchResultPage() {
      return this.$route.name?.includes('searchResults')
    },
    isProductDataPage() {
      return this.$route.name?.includes('categories')
        || this.$route.name?.includes('first-level-category')
        || this.$route.name?.includes('second-level-category')
        || this.$route.name?.includes('third-level-category')
        || this.$route.name?.includes('products-product')
    },
    appVersion() {
      return (this.$config.public.environment || 'DEV') + '(' + this.$globalization.getRegion() + ') - ' + (this.$config.public.release || 'DEV')
    },
    hasCompanies() {
      if (this.companies) {
        return this.companies.length > 0
      }
      return false
    },
    customerRegion() {
      let salesAreaName = this.selectedCustomer?.salesOrg + '-' + this.selectedCustomer?.salesChannel + '-' + this.selectedCustomer?.distribution
      return this.selectedCustomer && getRegionForSalesArea(salesAreaName)
    },
    brandExclusion() {
      if (this.excludedBrands?.length > 0) {
        if (this.excludedBrands?.length === 1) {
          return ` nobrand-${this.excludedBrands.join('')}`
        }
        return ` nobrand-${this.excludedBrands.join('')} nobrand-${this.excludedBrands.join(' nobrand-')}`
      }
      return null
    },
    showAddressDropDown() {
      if (this.addresses) {
        return this.addresses?.length > 1 && (this.isSearchResultPage || this.isProductDataPage)
      }
      return false
    },
    isImpersonated() {
      if (this.accessToken) {
        const decodedToken = jwt_decode(this.accessToken)
        return decodedToken.extension_isimpersonated === '1'
      }
      return false
    },
    impersonatedText() {
      if (this.isImpersonated) {
        const decodedToken = jwt_decode(this.accessToken)
        return 'Impersonated: ' + decodedToken.email
      }
      return ''
    },
    showTariffDisclaimer() {
      return this.isTariffEnabled && this.isTariffDisclaimerEnabled && !this.acceptedTariffDisclaimer
    },
    shouldFetchTargetedMessages() {
      return this.$auth.loggedIn
        && this.selectedCustomer?.customerNo
        && !this.isLoadingUserData
        && !this.isSettingCustomerData
    },
    headerHeight() {
      if (this.isMobile) {
        return '0px'
      }
      const baseHeight = 220
      return `${baseHeight + this.importantMessagesHeight}px`
    }
  },
  data() {
    const localizationProvider = {}
    const i18n = useI18nExtended()
    Object.defineProperties(localizationProvider, {
      language: {
        enumerable: true,
        value: i18n.locale.value
      },
      toLanguageString: {
        enumerable: true,
        value: (str, def) => {
          if (this.$te(`kendo.${str}`)) {
            return this.$tv(str, 'kendo')
          }

          return (this.$te(str) ? this.$tv(str) : def)}
      }
    })

    let intlProvider = new IntlService(i18n.locale.value)
    return {
      navigationIsOpened: false,
      localizationProvider,
      intlProvider,
      contactFormOverlay: false,
      contactFormPrefilledMessage: '',
      contactFormTopicId: '',
      contactFormTrackingCallback: null,
      isCustomerSet: true,
      isReady: false,
      showBrandsPopup: false,
      popupSizeMedium: POPUP.Size.medium,
      brands: [],
      userInteracted: false,
      addresses: [],
      componentKey: 0,
      scrollPosition: 0,
      isScrolled: false,
      isMobile: false,
      showHydraulicSelectorForm: false,
      hydraulicSelectorData: {},
      popupSize: POPUP.Size,
      importantMessagesHeight: 0,
      resizeObserver: null
    }
  },
  methods: {
    setupImportantMessagesObserver() {
      if (!this.importantMessages?.length && !this.isImpersonated) {
        this.importantMessagesHeight = 0
        return
      }
      // Use setTimeout because ImportantMessages is inside <client-only> in Header
      // and may not be rendered immediately after $nextTick
      setTimeout(() => {
        let totalHeight = 0

        // Measure impersonation div if present
        const impersonationEl = document.querySelector('.impersonation')
        if (impersonationEl) {
          totalHeight += impersonationEl.offsetHeight
        }

        // Measure important messages div inside the fixed header
        const importantMessagesEl = document.querySelector('.fixed-header.header > div')
        if (importantMessagesEl) {
          if (this.resizeObserver) {
            this.resizeObserver.disconnect()
          }
          this.resizeObserver = new ResizeObserver((entries) => {
            let observedHeight = 0
            for (const entry of entries) {
              observedHeight = entry.target.offsetHeight
            }
            // Add impersonation height if present
            const impEl = document.querySelector('.impersonation')
            const impHeight = impEl ? impEl.offsetHeight : 0
            this.importantMessagesHeight = observedHeight + impHeight
          })
          this.resizeObserver.observe(importantMessagesEl)
          // Initial measurement
          totalHeight += importantMessagesEl.offsetHeight
        }

        this.importantMessagesHeight = totalHeight
      }, 100)
    },
    updateUrlWithCustomerUuid(customerUuid) {
      try {
        const url = new URL(window.location)
        if (url.searchParams.get('customerUuid') === customerUuid) {
          return
        }

        url.searchParams.set('customerUuid', customerUuid)
        window.history?.replaceState({}, '', url)
      } catch {
        // Silent fail - shouldn't happen in modern browsers
      }
    },
    closeHydraulicSelectorForm() {
      this.showHydraulicSelectorForm = false
      this.hydraulicSelectorData = {}
      this.$gtm.trackEvent({
        event: 'hydra_contactform_sendrequest',
        category: 'hydraulic-selector',
        action: 'close_contact_form'
      })
    },
    handleScroll() {
      this.isScrolled = window.scrollY === 0
    },
    async reloadTranslations() {
      this.$eventbus.emit('loading', true)
      await this.$i18n.waitForPendingLocaleChange()
      await this.$nextTick()
      this.intlProvider.locale = this.$i18n.locale
      this.$eventbus.emit('loading', false)
    },
    openPopup(action) {
      if (action === SIDEBAR_ACTIONS.Contact) {
        this.contactFormOverlay = true
      }
      if (action === SIDEBAR_ACTIONS.Feedback) {
        //currently not used
      }
    },
    clearFormData() {
      this.contactFormPrefilledMessage = ''
      this.contactFormTopicId = ''
      this.contactFormTrackingCallback = null
    },
    closeContactForm() {
      this.contactFormOverlay = false
      this.clearFormData()
    },
    sendGTMEvent() {
      this.$gtm.trackEvent({
        event: 'submit',
        category: 'feedback',
        action: 'submit_feedback_form',
        value: null
      })
    },
    setEventListener() {
      const el = document.getElementById('surveySubmitBtn')
      if (el) {
        el.addEventListener('click', this.sendGTMEvent)
      }
    },
    handleCustomerChange() {
      this.isCustomerSet = !this.isCustomerSet
      !this.isCustomerSet ? this.$eventbus.emit('loading', true) : this.$eventbus.emit('loading', false)
    },
    loadLocaleData() {
      let localeMain = main[this.$i18n.locale]
      load({supplemental}, {main: {[this.$i18n.locale]: localeMain}})
    },
    async loadBrandsData() {
      await useProductDataStore().fetchAllBrands()
      this.brands = []
      let freudenberg = this.getBrandByCode('F', this.$i18n.locale)
      if (freudenberg !== undefined) {
        this.brands.push(freudenberg)
      }
      let dichtomatik = this.getBrandByCode('D', this.$i18n.locale)
      if (dichtomatik !== undefined) {
        this.brands.push(dichtomatik)
      }
    },
    async fetchAddresses() {
      let allAddresses = await (this.getAddresses?.(this.currentCustomerUuid) ?? [])
      if (allAddresses && allAddresses.length > 0) {
        allAddresses = allAddresses.map(a => ({...a, dropDownName: `${a.name}, ${a.street}, ${a.zip} ${a.city}${a.addressRegion !== null ? ` ${a.addressRegion},` : ','} ${this.$tv(a.countryShortCode, 'countries')}${a.erpReference ? (' (' + a.erpReference + ')') : ''}`}))
        this.addresses = allAddresses.filter(a => a.addressType === ADDRESS_TYPE.Delivery)
        if (!this.selectedAddresses || !this.selectedAddresses.delivery) {
          const firstDeliveryAddress = this.addresses.find(a => a.addressType === ADDRESS_TYPE.Delivery)
          if (firstDeliveryAddress) {
            this.setAddressDelivery(firstDeliveryAddress)
          }
        } else {
          if (this.selectedAddresses?.delivery && !this.addresses.some(a => a.addressUuid === this.selectedAddresses.delivery.addressUuid)) {
            let address = this.addresses.find(a => a.addressType === ADDRESS_TYPE.Delivery)
            if (address) {
              await this.setSelectedAddress({address: address, type: ADDRESS_TYPE.Delivery})
            }
          }
        }
      }
    },
    setAddressDelivery(address) {
      this.cartStore.setSelectedAddress({address: address, type: ADDRESS_TYPE.Delivery})
    },
    dismissTargetedMessage(index) {
      const message = this.targetedMessages[index]
      const messageId = message.id
      this.targetedMessagesStore.dismissMessage(messageId)
    },
    async fetchTargetedMessages() {
      if (this.shouldFetchTargetedMessages) {
        const salesAreaName = `${this.selectedCustomer?.salesOrg}_${this.selectedCustomer?.salesChannel}_${this.selectedCustomer?.distribution}`

        const params = {
          salesArea: salesAreaName,
          companyNo: this.selectedCustomer?.customerNo,
          userType: this.isInternalUser ? 'internal' : 'external',
          _locale: this.$i18n.locale
        }

        await this.targetedMessagesStore.fetchMessages(params)
      }
    }
  },
  provide() {
    return {kendoIntlService: this.intlProvider, kendoLocalizationService: this.localizationProvider}
  },
  errorCaptured: function(err, _, info) {
    if (this.$gtm) {
      this.$gtm.trackEvent({
        event: 'exception',
        value: `${(err && err.message) || '-'}, info: ${info}`
      })
    }
  },
  updated: function () {
    this.$nextTick(function () {
      this.isReady = true
    })
  },
  created: function() {
    this.loadLocaleData()
    this.loadBrandsData()
    this.$eventbus.on('showBrandsPopup', () => this.showBrandsPopup = true)
    this.$eventbus.on('hideBrandsPopup', () => this.showBrandsPopup = false)
    this.$eventbus.on('showContactPopup', (payload) => {
      this.contactFormOverlay = true
      this.contactFormPrefilledMessage = payload?.prefilledMessage || ''
      this.contactFormTopicId = payload?.topicId || ''
      this.contactFormTrackingCallback = payload?.trackingCallback || null
    })
  },
  watch: {
    '$i18n.locale': {
      handler() {
        this.loadLocaleData()
        this.loadBrandsData()
        this.fetchTargetedMessages()
        if (this.$route?.matched?.length === 0) {
          let path = this.$route.path.split('/')
          if (path.length > 2 && path[2] !== this.$i18n.locale) {
            path[2] = this.$i18n.locale
            navigateTo(path.join('/'))
          }
        }
      }
    },
    currentCustomerUuid: {
      immediate: true,
      handler() {
        if (this.$auth.loggedIn) {
          this.fetchAddresses()
          this.componentKey += 1

          if (this.currentCustomerUuid && this.currentCustomerUuid !== 'default') {
            this.updateUrlWithCustomerUuid(this.currentCustomerUuid)
          }
        }
      }
    },
    '$route.fullPath': {
      immediate: true,
      handler() {
        if (this.$auth.loggedIn && this.currentCustomerUuid && this.currentCustomerUuid !== 'default') {
          this.updateUrlWithCustomerUuid(this.currentCustomerUuid)
        }
      }
    },
    showAddressDropDown: {
      immediate: true,
      handler(val) {
        this.cartStore?.setAddressDropdownVisibility(val)
      }
    },
    head: {
      immediate: true,
      handler(val) {
        if (!(this.$route?.name?.startsWith('categories-')
          || this.$route?.name?.startsWith('accumulator-selector')
          || this.$route?.name?.startsWith('simmerring-selector')
          || this.$route?.name?.startsWith('hydraulic-selector')
          || this.$route?.name?.startsWith('oring-calculator'))) {
          useHead(val)
        }
      }
    },
    shouldFetchTargetedMessages: {
      immediate: true,
      handler(shouldFetch) {
        if (shouldFetch) {
          this.fetchTargetedMessages()
        }
      }
    },
    importantMessages: {
      handler() {
        this.$nextTick(() => {
          this.setupImportantMessagesObserver()
        })
      }
    }
  }
}
</script>

<style lang="scss">
.noImportantMessages {
  padding-top: 5rem;
}

.head {
  display: flex;
  flex-direction: column;

  @include breakpoint-down($md) {
    &__spacing--xs {
      margin-top: 3.625rem;
    }

    &__spacing--s {
      margin-top: 5.75rem;
    }

    &__spacing--m {
      margin-top: 3.5rem;
    }

    &__spacing--l {
      margin-top: 5.725rem;
    }
  }

  &__notification {
    display: none;

    @include breakpoint-up($md) {
      display: block;
    }
  }

  &__notification__mobile {
    display: none;

    @include breakpoint-down($md) {
      display: block;
    }
  }

  .hydraus-form {
    width: 50rem;

    .popup__wrapper {
      width: auto;
    }
  }
}

.head:has(.mobile-menu.open) {
  .sticky {
    display: none;
  }
}

body:has(.mobile-menu.open) {
  overflow: hidden;

  .bottom__overlay {
    display: none;
  }
}

.bottom__overlay {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 300;
  height: 2.625rem;
  background-color: $color__text__search;
  padding-top: .25rem;
  padding-bottom: .125rem;

  @include breakpoint-down($md) {
    height: 3.75rem;
  }
}

.addressMargin {
  margin-bottom: 2.625rem;
}

#appversion {
  color: $color__text__search;
  font-size: .6rem;
  font-family: 'Courier New', Courier, monospace;
  position: fixed;
  bottom: 0;
  right: 0;
  pointer-events: none;
  background: none;
  letter-spacing: normal;
}

@include breakpoint-up($xl) {
  .contactform .popup__wrapper {
    margin: 1.5rem auto;
  }
}

.impersonation {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 13;
  background-color: $color__text--error;
  color: $color__white;
  text-align: center;
  padding: 0.25rem 0;
}

.head:has(.impersonation) {
  .fixed-header {
    top: 1.75rem;
  }
}

// Adjust sticky navigation when impersonation banner is present
.root:has(.impersonation) {
  .category-navigation {
    top: 8.65rem !important; // 6.9rem + 1.75rem for impersonation banner height
  }
}
</style>
